import { useEffect, useMemo, useState } from 'react';
import { X, Settings, RefreshCw, Activity, Wifi, Clock, User, Info } from 'lucide-react';

const API_BASE = 'https://app.24review.de';
const token = () => localStorage.getItem('token');

function formatTime(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return '—';
  return date.toLocaleTimeString('de-DE', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

function formatAge(value) {
  if (!value) return '—';
  const date = new Date(value);
  const diff = Math.max(0, Date.now() - date.getTime());
  const sec = Math.round(diff / 1000);
  if (sec < 60) return `vor ${sec}s`;
  const min = Math.round(sec / 60);
  if (min < 60) return `vor ${min}m`;
  return formatTime(value);
}

function queueLabel(row) {
  const type = String(row?.type || '').toLowerCase();
  const queue = String(row?.queue || '').toLowerCase();
  if (type === 'pv' || queue === 'potluck') return 'PV';
  if (type === 'category' || row?.category_id) return `Kategorie${row?.page ? ` Seite ${row.page}` : ''}`;
  if (type === 'search' || queue === 'search') return row?.search_term ? `Keyword: ${row.search_term}` : 'Keyword';
  if (type === 'ai' || queue === 'all_items') return `AI${row?.page ? ` Seite ${row.page}` : ''}`;
  return row?.queue || row?.type || '—';
}

function shortScannerId(id) {
  if (!id) return '—';
  return String(id).replace(/^scanner-/, '').slice(0, 8);
}

function accountLabel(scanner) {
  const account = scanner?.scanner_account_name || scanner?.scannerAccountName || '';
  const label = scanner?.scanner_label || scanner?.scannerLabel || '';
  const raw = account || label;
  if (raw) {
    const num = String(raw).match(/\d+/)?.[0];
    return num ? `Account ${num}` : raw;
  }
  return 'Scanner ohne Account';
}

function StatusPill({ active, children }) {
  return (
    <span style={{
      ...styles.pill,
      background: active ? 'rgba(34,197,94,0.13)' : 'rgba(148,163,184,0.12)',
      color: active ? '#86efac' : 'var(--text3)',
      borderColor: active ? 'rgba(34,197,94,0.35)' : 'var(--border)',
    }}>
      {children}
    </span>
  );
}

function fmtNum(value) {
  const n = Number(value || 0);
  return Number.isFinite(n) ? n.toLocaleString('de-DE') : '0';
}

export default function ScannerConfigModal({ isOpen, onClose }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const fetchStatus = async () => {
    setLoading(true);
    setError('');

    try {
      const res = await fetch(`${API_BASE}/scan/scanners/active?minutes=2`, {
        headers: token() ? { Authorization: `Bearer ${token()}` } : {},
      });
      const json = await res.json();
      if (!json.ok) throw new Error(json.error || 'Status konnte nicht geladen werden');
      setData(json);
      setLastUpdated(new Date());
    } catch (err) {
      setError(err.message || 'Verbindungsfehler');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    fetchStatus();
    const t = setInterval(fetchStatus, 15000);
    return () => clearInterval(t);
  }, [isOpen]);

  const scannerRows = useMemo(() => {
    const map = new Map();

    for (const row of data?.connectedScanners || data?.scanners || []) {
      if (!row.scanner_id) continue;
      map.set(row.scanner_id, {
        scanner_id: row.scanner_id,
        scanner_account_name: row.scanner_account_name,
        scanner_label: row.scanner_label,
        last_seen: row.last_seen,
        connected: true,
        active: false,
        recent: false,
      });
    }

    for (const row of data?.activeWorkers || []) {
      if (!row.scanner_id) continue;
      const existing = map.get(row.scanner_id) || { scanner_id: row.scanner_id };
      map.set(row.scanner_id, {
        ...existing,
        ...row,
        active: true,
        connected: existing.connected !== false,
      });
    }

    for (const row of data?.recentScanners || []) {
      if (!row.scanner_id) continue;
      const existing = map.get(row.scanner_id) || { scanner_id: row.scanner_id };
      map.set(row.scanner_id, {
        ...existing,
        ...row,
        recent: true,
        connected: existing.connected !== false,
      });
    }

    return Array.from(map.values()).sort((a, b) => {
      if (a.active !== b.active) return a.active ? -1 : 1;
      if (a.recent !== b.recent) return a.recent ? -1 : 1;
      return new Date(b.last_scan_at || b.last_seen || 0) - new Date(a.last_scan_at || a.last_seen || 0);
    });
  }, [data]);

  if (!isOpen) return null;

  const activeCount = data?.recentScannerCount ?? data?.activeWorkerCount ?? 0;
  const workerCount = data?.activeWorkerCount ?? 0;
  const connectedCount = data?.connectedCount ?? data?.count ?? 0;

  return (
    <div style={styles.overlay} onMouseDown={(e) => e.target === e.currentTarget && onClose?.()}>
      <div style={styles.modal}>
        <div style={styles.header}>
          <div style={styles.titleWrap}>
            <Settings size={22} color="var(--accent)" />
            <div>
              <div style={styles.title}>Scanner Übersicht</div>
              <div style={styles.subtitle}>Status der verbundenen Extension-Profile</div>
            </div>
          </div>

          <button style={styles.iconBtn} onClick={onClose} title="Schließen">
            <X size={20} />
          </button>
        </div>

        <div style={styles.body}>
          <div style={styles.notice}>
            <Info size={18} style={{ flexShrink: 0, marginTop: 1 }} />
            <div>
              <strong>Einstellungen werden nur in der Extension geändert.</strong>
              <div style={{ marginTop: 4 }}>
                Account-Auswahl, PV, ZA AI, Kategorie, Search und Telegram-Ziele werden lokal im jeweiligen Chrome-Profil eingestellt. Dieses Fenster zeigt nur den aktuellen Status.
              </div>
            </div>
          </div>

          <div style={styles.summaryGrid}>
            <div style={styles.summaryCard}>
              <Activity size={16} color="#86efac" />
              <div>
                <div style={styles.summaryValue}>{activeCount}</div>
                <div style={styles.summaryLabel}>Scanner scannen</div>
              </div>
            </div>
            <div style={styles.summaryCard}>
              <Clock size={16} color="#93c5fd" />
              <div>
                <div style={styles.summaryValue}>{workerCount}</div>
                <div style={styles.summaryLabel}>Job aktiv</div>
              </div>
            </div>
            <div style={styles.summaryCard}>
              <Wifi size={16} color="var(--accent)" />
              <div>
                <div style={styles.summaryValue}>{connectedCount}</div>
                <div style={styles.summaryLabel}>verbunden</div>
              </div>
            </div>
          </div>

          <div style={styles.toolbar}>
            <div style={styles.updatedText}>
              Zuletzt aktualisiert: {lastUpdated ? formatTime(lastUpdated) : '—'}
            </div>
            <button style={{ ...styles.refreshBtn, opacity: loading ? 0.65 : 1 }} onClick={fetchStatus} disabled={loading}>
              <RefreshCw size={14} style={{ animation: loading ? 'spin 1s linear infinite' : 'none' }} />
              {loading ? 'Aktualisiere...' : 'Aktualisieren'}
            </button>
          </div>

          {error && <div style={styles.error}>{error}</div>}

          <div style={styles.list}>
            {scannerRows.length === 0 && !loading ? (
              <div style={styles.empty}>Keine Scanner in den letzten 2 Minuten gesehen.</div>
            ) : (
              scannerRows.map((scanner) => (
                <div key={scanner.scanner_id} style={styles.scannerCard}>
                  <div style={styles.scannerTop}>
                    <div style={styles.accountBlock}>
                      <User size={15} color="var(--accent)" />
                      <div>
                        <div style={styles.accountName}>{accountLabel(scanner)}</div>
                        <div style={styles.scannerId}>{scanner.scanner_account_name || scanner.scanner_label ? `ID ${shortScannerId(scanner.scanner_id)}` : `Kein Account gewählt · ID ${shortScannerId(scanner.scanner_id)}`}</div>
                      </div>
                    </div>

                    <div style={styles.pillRow}>
                      <StatusPill active={scanner.active || scanner.recent}>
                        {scanner.active || scanner.recent ? 'scannt' : 'verbunden'}
                      </StatusPill>
                    </div>
                  </div>

                  <div style={styles.detailsGrid}>
                    <div>
                      <div style={styles.detailLabel}>Letzter Scan</div>
                      <div style={styles.detailValue}>{scanner.last_scan_at ? formatAge(scanner.last_scan_at) : formatAge(scanner.last_seen)}</div>
                    </div>
                    <div>
                      <div style={styles.detailLabel}>Letzter Job</div>
                      <div style={styles.detailValue}>{queueLabel(scanner)}</div>
                    </div>
                    <div>
                      <div style={styles.detailLabel}>Neue ASINs heute</div>
                      <div style={styles.detailValue}>{fmtNum(scanner.today_new_found)}</div>
                    </div>
                    <div>
                      <div style={styles.detailLabel}>Neue ASINs gesamt</div>
                      <div style={styles.detailValue}>{fmtNum(scanner.total_new_found)}</div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        <div style={styles.footer}>
          <button style={styles.closeBtn} onClick={onClose}>Schließen</button>
        </div>
      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.58)',
    backdropFilter: 'blur(3px)',
    zIndex: 10000,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 18,
  },
  modal: {
    width: 'min(860px, 96vw)',
    maxHeight: '88vh',
    background: 'var(--bg2)',
    border: '1px solid var(--border)',
    borderRadius: 18,
    color: 'var(--text)',
    boxShadow: '0 24px 80px rgba(0,0,0,0.35)',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  header: {
    padding: '18px 22px',
    borderBottom: '1px solid var(--border)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    background: 'var(--bg3)',
  },
  titleWrap: { display: 'flex', alignItems: 'center', gap: 12 },
  title: { fontSize: 20, fontWeight: 800, fontFamily: 'Syne' },
  subtitle: { marginTop: 2, fontSize: 12, color: 'var(--text3)' },
  iconBtn: {
    background: 'transparent',
    border: 0,
    color: 'var(--text3)',
    cursor: 'pointer',
    padding: 7,
    borderRadius: 8,
    display: 'flex',
  },
  body: {
    padding: 18,
    overflowY: 'auto',
  },
  notice: {
    display: 'flex',
    gap: 10,
    padding: '13px 15px',
    borderRadius: 12,
    border: '1px solid rgba(99,102,241,0.35)',
    background: 'rgba(99,102,241,0.10)',
    color: 'var(--text2)',
    fontSize: 13,
    lineHeight: 1.45,
    marginBottom: 14,
  },
  summaryGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    gap: 10,
    marginBottom: 14,
  },
  summaryCard: {
    background: 'var(--bg3)',
    border: '1px solid var(--border)',
    borderRadius: 12,
    padding: '12px 14px',
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
  summaryValue: { fontSize: 22, lineHeight: 1, fontWeight: 800, fontFamily: 'Syne' },
  summaryLabel: { fontSize: 11, color: 'var(--text3)', marginTop: 3 },
  toolbar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 12,
    marginBottom: 12,
  },
  updatedText: { color: 'var(--text3)', fontSize: 12 },
  refreshBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 7,
    padding: '8px 12px',
    background: 'var(--bg3)',
    border: '1px solid var(--border)',
    borderRadius: 9,
    color: 'var(--text2)',
    cursor: 'pointer',
    fontSize: 12,
    fontWeight: 700,
  },
  error: {
    marginBottom: 12,
    padding: '10px 12px',
    borderRadius: 10,
    color: '#fecaca',
    background: 'rgba(239,68,68,0.14)',
    border: '1px solid rgba(239,68,68,0.25)',
    fontSize: 13,
  },
  list: { display: 'grid', gap: 10 },
  empty: {
    padding: 26,
    textAlign: 'center',
    color: 'var(--text3)',
    background: 'var(--bg3)',
    border: '1px dashed var(--border)',
    borderRadius: 12,
  },
  scannerCard: {
    background: 'var(--bg)',
    border: '1px solid var(--border)',
    borderRadius: 13,
    padding: 14,
  },
  scannerTop: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 10,
    marginBottom: 12,
  },
  accountBlock: { display: 'flex', alignItems: 'center', gap: 10 },
  accountName: { fontSize: 15, fontWeight: 800, color: 'var(--text)' },
  scannerId: { fontSize: 11, color: 'var(--text3)', marginTop: 2 },
  pillRow: { display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' },
  pill: {
    padding: '4px 9px',
    border: '1px solid',
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 800,
    textTransform: 'uppercase',
    letterSpacing: '0.3px',
  },
  detailsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
    gap: 10,
  },
  detailLabel: {
    fontSize: 10,
    color: 'var(--text3)',
    textTransform: 'uppercase',
    letterSpacing: '0.4px',
    marginBottom: 4,
  },
  detailValue: {
    color: 'var(--text2)',
    fontSize: 12,
    fontWeight: 700,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  footer: {
    padding: '14px 18px',
    borderTop: '1px solid var(--border)',
    background: 'var(--bg3)',
    display: 'flex',
    justifyContent: 'flex-end',
  },
  closeBtn: {
    padding: '10px 18px',
    borderRadius: 10,
    border: '1px solid var(--border)',
    background: 'var(--accent)',
    color: 'white',
    cursor: 'pointer',
    fontWeight: 800,
  },
};
